import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LifeBuoy, MousePointer2, ScreenShare, X } from 'lucide-react';
import { Modal } from '../Modal';
import { useToast } from '../../contexts/ToastContext';
import { socketService } from '../../services/socketService';
import {
  InjectPump,
  NativeCapture,
  PcSummary,
  RemoteSupportTarget,
  RsPeer,
  RsSignal,
  SessionLayout,
  listenNative,
  onRemoteSupportAsk,
  rsInvoke,
  startNativeCapture,
} from '../../services/remoteSupportService';
import { flashTaskbar } from '../../utils/tauri';
import { resolveAvatarUrl } from '../../utils/serverUrl';
import { handleAvatarError } from '../../types';
import { RemoteInputEvent } from '../../utils/remoteInput';
import { captureBitrate, captureLabel } from '../../utils/remoteCaptureProtocol';
import { endReasonLabel, filesLabel, formatDuration } from '../../utils/remoteSupportLabels';
import { RemoteFileHost } from './RemoteFileHost';

/**
 * 원격지원 — 직원(화면을 내주는 쪽, PC 앱 전용).
 *
 * 흐름: rs:incoming → Rust 네이티브 보기 동의창([공유]) → rs:accept → 테두리·상태 바(Rust) →
 *       모든 모니터 송출(Rust 캡처 → startNativeCapture) → rs:started(세션 번호) → offer → 담당자 보기
 *       rs:control:requested → Rust 네이티브 동의창 → 게이트 토큰 → rs:control:granted → 입력 주입
 *
 * 화면은 고르지 않는다 — 모든 모니터(가상 데스크톱)가 한 프레임으로 간다(2026-10-08, docs §14). 예전 getDisplayMedia
 * 선택 창이 하던 '사람이 직접 눌러야 화면이 나간다'는 보장은 네이티브 보기 동의창이 맡는다(JS가 누를 수 없다).
 * ⚠ 그 앞에 JS 동의 모달을 다시 두지 말 것(docs §15) — 데스크톱 앱은 CSP가 없어 JS 모달은 보안상 아무것도
 *   지키지 못하고 직원의 클릭만 2번으로 늘린다. 누가 요청했는지·무엇이 넘어가는지의 고지는 Rust `consent_text`가 한다.
 *
 * 직원이 먼저 요청하는 경로: 담당자 프로필 '원격지원 요청' → askRemoteSupport 버스 → rs:ask → 대기 카드(asking) →
 *       담당자 수락 → rs:incoming{asked:true} → 같은 네이티브 동의창을 '원격지원 시작' 문구로 → 이후 위와 같다.
 *       담당자가 늦게 수락했을 때 자리를 비운 사이 화면이 나가지 않게, 공유 직전에 직원이 직접 [공유]를 누른다.
 *
 * 끝내는 길은 넷이다: 화면 위 상태 바(Rust) · 트레이 '원격지원 종료' · Ctrl+Alt+Shift+Q · 앱 안 배너.
 * 앞의 셋은 Rust가 먼저 게이트를 닫고 이벤트로 알려 오므로, JS가 멈춰 있어도 제어는 즉시 끊긴다.
 */

/**
 * consenting: 네이티브 보기 동의창이 떠 있다(서버 상태 requested — [공유]를 누르면 rs:accept로 picking)
 * starting: [공유]를 눌렀고 송출을 여는 중(서버 picking)
 */
type Phase = 'idle' | 'asking' | 'consenting' | 'starting' | 'viewing';

interface Incoming {
  sessionId: string;
  operator: { name: string; avatar: string | null; depart: string | null };
  /** 서버 요청의 남은 시간 — 네이티브 동의창 카운트다운을 서버 만료에 맞춘다 */
  expiresInMs: number;
  /** 내가 먼저 요청했고 담당자가 수락했다 — 문구만 '원격지원 시작'으로 바뀐다 */
  asked: boolean;
}

/** 내가 담당자에게 요청하고 수락을 기다리는 중 */
interface Asking {
  sessionId: string;
  target: RemoteSupportTarget;
  expiresAt: number;
}

interface Summary {
  operatorName: string;
  code: string | null;
  durationSeconds: number | null;
  controlSeconds: number;
  reason: string;
  /** 주고받은 파일 수(서버 rs:ended). 담당자 → 직원 / 직원 → 담당자 */
  filesToHost?: number;
  filesToOperator?: number;
}

/** 서버가 보낸 사유 → 직원에게 보일 안내 */
const CONTROL_END_TOAST: Record<string, string> = {
  host_revoked: '제어를 되찾았습니다. 담당자는 화면만 볼 수 있습니다.',
  operator_returned: '담당자가 제어를 반납했습니다.',
  expired: '제어 시간(30분)이 끝나 제어를 되찾았습니다.',
  idle: '담당자의 입력이 한동안 없어 제어를 되찾았습니다.',
  display_changed: '화면 구성이 바뀌어 제어를 되찾았습니다.',
  policy_off: '관리자가 원격 제어를 막아 제어를 되찾았습니다.',
};

export const RemoteSupportHost: React.FC = () => {
  const { showToast } = useToast();
  const [phase, setPhase] = useState<Phase>('idle');
  const [incoming, setIncoming] = useState<Incoming | null>(null);
  const [asking, setAsking] = useState<Asking | null>(null);
  const [controlling, setControlling] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [summary, setSummary] = useState<Summary | null>(null);
  /** 파일 주고받기 채널 'fx'(RemoteFileHost가 소유) — 구버전 담당자와도 만들어진다. 세션이 끝나면 null */
  const [fileChannel, setFileChannel] = useState<RTCDataChannel | null>(null);

  // 이벤트 핸들러가 최신 값을 읽도록 ref로도 들고 있는다(구독은 마운트 1회)
  const sessionRef = useRef<string | null>(null);
  const operatorRef = useRef<string>('');
  const codeRef = useRef<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  /** Rust 화면 캡처(모든 모니터) — 멈추면 Rust 쪽 송출도 세션 종료와 함께 끝난다 */
  const captureRef = useRef<NativeCapture | null>(null);
  const peerRef = useRef<RsPeer | null>(null);
  const tokenRef = useRef<string | null>(null);
  const deviceIdRef = useRef<string | null>(null);
  const endedRef = useRef(false);
  const pumpRef = useRef<InjectPump | null>(null);
  const startedAtRef = useRef<number | null>(null);
  /** rs:ask ack를 기다리는 중 — 연타로 요청이 두 번 나가지 않게 */
  const askPendingRef = useRef(false);

  // 기기 ID는 미리 받아 둔다 — [공유] 뒤에 기다리면 화면이 늦게 나간다
  useEffect(() => {
    rsInvoke<string>('get_or_create_device_id').then(id => { deviceIdRef.current = id; }).catch(() => {});
    // 앱이 새로 떴다면(새로고침·업데이트 재시작) 이전 세션의 네이티브 잔여물(테두리·열린 게이트)을 걷는다
    rsInvoke('rs_session_end').catch(() => {});
  }, []);

  // 경과 시간·요청 대기 카운트다운
  useEffect(() => {
    if (phase === 'idle') return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [phase]);

  const emit = (event: string, payload: Record<string, unknown>) =>
    socketService.emitRemoteSupport(event, payload);

  /** 서버 집계용 입력 건수 — 제어 회수·종료 때 실어 보낸다 */
  const takeInputs = () => pumpRef.current?.takeCount() ?? 0;

  /** 게이트 토큰을 잊는다(이미 Rust가 닫았거나 닫을 것) */
  const dropControl = useCallback(() => {
    tokenRef.current = null;
    pumpRef.current?.clear();
    setControlling(false);
  }, []);

  /** 모든 로컬 자원 정리 — 네이티브 창, 화면 송출, P2P */
  const cleanupLocal = useCallback(() => {
    tokenRef.current = null;
    pumpRef.current?.clear();
    peerRef.current?.close();
    peerRef.current = null;
    captureRef.current?.stop();
    captureRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    // 송출(Rust 캡처)·확인창·테두리를 함께 걷는다
    rsInvoke('rs_session_end').catch(() => {});
    flashTaskbar(true);
    sessionRef.current = null;
    setFileChannel(null);
    setControlling(false);
    setStartedAt(null);
    setIncoming(null);
    setAsking(null);
    setPhase('idle');
  }, []);

  const showSummary = useCallback((s: Summary) => {
    if (endedRef.current) return;
    endedRef.current = true;
    setSummary(s);
  }, []);

  /** 직원이 끝낸다(배너·상태 바·트레이·단축키·공유 중지) */
  const endSession = useCallback((reason: 'normal' | 'share_ended' = 'normal') => {
    const sessionId = sessionRef.current;
    if (!sessionId) return;
    emit('rs:end', { sessionId, reason, inputs: takeInputs() });
    const started = startedAtRef.current;
    cleanupLocal();
    // 서버의 rs:ended가 오면 그 숫자로 요약을 보여 준다. 끊긴 상태라 안 오면 로컬 값으로 대신한다
    window.setTimeout(() => {
      showSummary({
        operatorName: operatorRef.current,
        code: codeRef.current,
        durationSeconds: started ? Math.round((Date.now() - started) / 1000) : null,
        controlSeconds: 0,
        reason,
      });
    }, 3000);
  }, [cleanupLocal, showSummary]);

  useEffect(() => { startedAtRef.current = startedAt; }, [startedAt]);

  const decline = useCallback((reason: 'declined' | 'picker' = 'declined') => {
    const sessionId = sessionRef.current;
    if (sessionId) emit('rs:decline', { sessionId, reason });
    cleanupLocal();
  }, [cleanupLocal]);

  // ── 직원이 먼저 요청: 담당자 프로필 '원격지원 요청' → rs:ask → 수락 대기 ──

  useEffect(() => onRemoteSupportAsk((target) => {
    void (async () => {
      if (sessionRef.current || askPendingRef.current) {
        showToast({ type: 'info', message: '이미 진행 중인 원격지원이 있습니다. 먼저 종료해 주세요.' });
        return;
      }
      askPendingRef.current = true;
      const ack = await socketService.emitRemoteSupportWithAck<{
        ok: boolean;
        sessionId?: string;
        operatorName?: string;
        expiresInMs?: number;
        message?: string;
      }>('rs:ask', { targetUserId: target.userId, targetAffiliateId: target.affiliateId ?? undefined });
      askPendingRef.current = false;
      if (!ack.ok || !ack.sessionId) {
        showToast({ type: 'info', message: ack.message || '원격지원을 요청하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
        return;
      }
      sessionRef.current = ack.sessionId;
      operatorRef.current = ack.operatorName || target.name;
      codeRef.current = null;
      endedRef.current = false;
      setSummary(null);
      setNow(Date.now());
      setAsking({ sessionId: ack.sessionId, target, expiresAt: Date.now() + (Number(ack.expiresInMs) || 60000) });
      setPhase('asking');
    })();
  }), [showToast]);

  /** 수락을 기다리다 그만둔다 — 서버가 담당자 쪽 수락 카드를 닫는다 */
  const cancelAsk = useCallback(() => {
    const sessionId = sessionRef.current;
    if (sessionId) emit('rs:end', { sessionId, reason: 'normal' });
    cleanupLocal();
  }, [cleanupLocal]);

  /** PC 정보(모니터 구성·캡처 사각형 포함) — 동의 모달에 미리 고지한 항목만. 화면 구성이 바뀌면 다시 보낸다 */
  const sendPcInfo = useCallback((sessionId: string) => {
    rsInvoke<PcSummary>('rs_pc_summary')
      .then(info => emit('rs:info', { sessionId, payload: info }))
      .catch(() => {});
  }, []);

  // ── 네이티브 보기 동의([공유] 한 번) → 허용 → 모든 모니터 송출 → 시작 ──

  const startAllScreens = useCallback(async (inc: Incoming) => {
    const { sessionId } = inc;
    if (sessionRef.current !== sessionId) return;
    // 1) Rust가 직접 띄우는 보기 동의창 — 원격지원을 시작하는 유일한 확인이다(JS 동의 모달 없음, docs §15).
    //    사람이 [공유]를 눌러야 세션(테두리·상태 바)이 생기고 캡처가 열린다
    let layout: SessionLayout;
    try {
      layout = await rsInvoke<SessionLayout>('rs_session_begin', {
        sessionId,
        operatorName: inc.operator.name,
        operatorDepart: inc.operator.depart,
        asked: inc.asked,
        timeoutMs: inc.expiresInMs,
      });
    } catch (err) {
      // 기다리는 사이 요청이 끝났다(담당자 취소·만료·다른 기기에서 공유·배너 거절) — cleanupLocal이 이미 확인창을 걷었다
      if (sessionRef.current !== sessionId) return;
      const e = String(err);
      if (e.includes('declined')) {
        decline('declined');
      } else if (e.includes('timeout')) {
        // 서버의 요청 타이머가 같은 시각에 'timeout'으로 마감한다 — rs:decline을 보내면 '거절'로 잘못 남는다
        showToast({ type: 'info', message: '응답이 없어 원격지원 요청이 만료되었습니다.' });
        cleanupLocal();
      } else {
        if (!e.includes('canceled')) {
          console.error('[RemoteSupport] native consent failed:', err);
          showToast({ type: 'error', message: '원격지원 확인창을 띄우지 못해 시작하지 않았습니다. 앱을 다시 시작한 뒤 요청받아 주세요.' });
        }
        decline('declined');
      }
      return;
    }
    if (sessionRef.current !== sessionId) {
      rsInvoke('rs_session_end').catch(() => {});
      return;
    }

    // 2) [공유]를 눌렀다 — 먼저 누른 이 PC가 화면을 내주는 기기로 고정된다(다른 PC의 확인창은 rs:handled로 닫힌다)
    const accepted = await socketService.emitRemoteSupportWithAck<{ ok: boolean; message?: string }>('rs:accept', {
      sessionId,
      deviceId: deviceIdRef.current,
    });
    if (!accepted.ok || sessionRef.current !== sessionId) {
      // 확인창을 누르는 사이 요청이 끝났다 — 방금 생긴 테두리·상태 바를 걷는다
      if (sessionRef.current === sessionId) {
        showToast({ type: 'info', message: accepted.message || '이미 끝난 요청입니다.' });
        cleanupLocal();
      } else {
        rsInvoke('rs_session_end').catch(() => {});
      }
      return;
    }
    flashTaskbar(true);
    setPhase('starting');

    // 2) 송출 — 모든 모니터가 트랙 하나
    let cap: NativeCapture;
    try {
      cap = await startNativeCapture(sessionId);
    } catch (err) {
      console.error('[RemoteSupport] capture failed:', err);
      if (sessionRef.current !== sessionId) return;
      showToast({ type: 'error', message: '화면을 보내지 못해 원격지원을 시작하지 않았습니다. 앱을 다시 시작한 뒤 요청받아 주세요.' });
      decline('picker');
      return;
    }
    if (sessionRef.current !== sessionId) {
      cap.stop();
      return;
    }
    captureRef.current = cap;
    streamRef.current = cap.stream;
    const { capture } = layout;

    const ack = await socketService.emitRemoteSupportWithAck<{ ok: boolean; code?: string; message?: string; startedAt?: number }>(
      'rs:started',
      // 기록·감사에 남는 화면 설명 — '전체 2대 3840x1088'
      { sessionId, monitorLabel: captureLabel(layout.monitors.length, capture.width, capture.height) }
    );
    if (!ack.ok || sessionRef.current !== sessionId) {
      showToast({ type: 'error', message: ack.message || '원격지원을 시작하지 못했습니다.' });
      cleanupLocal();
      return;
    }
    codeRef.current = ack.code ?? null;
    setStartedAt(Date.now());
    setPhase('viewing');

    const peer = new RsPeer('host', {
      onSignal: (data: RsSignal) => emit('rs:signal', { sessionId, data }),
      onInput: (events: RemoteInputEvent[]) => pumpRef.current?.push(events),
      onFileChannel: (ch) => setFileChannel(ch),
      onConnectionState: (state) => {
        if (state === 'failed') {
          showToast({ type: 'error', message: '담당자와 연결하지 못했습니다. 원격지원을 끝냅니다.' });
          endSession('normal');
        }
      },
    });
    peerRef.current = peer;
    try {
      // 축소는 Rust가 이미 했다(모니터당 폭 1920·화소 4K 한 장 상한) — 비트레이트만 크기에 맞춘다.
      // maxFramerate는 상한일 뿐이다: 실제 장수는 Rust가 정한다(제어 중 30·보기만 10, 화면이 그대로면 1초에 1장).
      // 10으로 두면 제어 중 원격 커서가 초당 10번만 움직인다(docs §16)
      await peer.startHost(cap.stream, { maxBitrate: captureBitrate(capture.width, capture.height), maxFramerate: 30 });
    } catch (err) {
      console.error('[RemoteSupport] startHost failed:', err);
      endSession('normal');
      return;
    }
    sendPcInfo(sessionId);
  }, [cleanupLocal, decline, endSession, sendPcInfo, showToast]);

  // ── 소켓 이벤트 ──

  useEffect(() => {
    return socketService.subscribeRemoteSupport((event, payload) => {
      const sid = payload?.sessionId;
      switch (event) {
        case 'rs:incoming': {
          // 이미 다른 요청을 처리 중이다(서버도 막는다). 내가 요청해 기다리던 세션이면 담당자가 수락한 것이다
          if (sessionRef.current && sessionRef.current !== sid) return;
          sessionRef.current = sid;
          operatorRef.current = payload.operator?.name || '담당자';
          codeRef.current = null;
          endedRef.current = false;
          setSummary(null);
          const inc: Incoming = {
            sessionId: sid,
            operator: {
              name: payload.operator?.name || '담당자',
              avatar: payload.operator?.avatar ?? null,
              depart: payload.operator?.depart ?? null,
            },
            expiresInMs: Number(payload.expiresInMs) || 60000,
            asked: payload.asked === true,
          };
          setIncoming(inc);
          setAsking(null);
          setPhase('consenting');
          // 다른 창에 가려져 있어도 알아차리게 — 창을 앞으로(배너에 담당자 사진) + 작업표시줄 점멸.
          // 확인창은 주 모니터 맨 위에 따로 뜨고 포커스를 뺏지 않는다
          rsInvoke('rs_bring_to_front').catch(() => {});
          flashTaskbar(false);
          void startAllScreens(inc);
          return;
        }
        case 'rs:handled': {
          if (sid !== sessionRef.current) return;
          const who = operatorRef.current || '담당자';
          const handledToast: Record<string, string> = {
            other_device: '다른 기기에서 원격지원 요청을 받았습니다.',
            canceled: '담당자가 원격지원 요청을 취소했습니다.',
            timeout: '응답이 없어 원격지원 요청이 만료되었습니다.',
            policy_off: '관리자가 원격지원을 꺼 요청이 취소되었습니다.',
            operator_revoked: '원격지원 요청이 취소되었습니다.',
            disconnect: '연결이 끊겨 원격지원 요청이 취소되었습니다.',
            // 내가 먼저 요청한 경우
            ask_declined: `${who}님이 지금은 원격지원을 할 수 없습니다. 1:1 대화로 문의해 주세요.`,
            ask_timeout: `${who}님이 응답하지 않아 요청이 만료되었습니다. 1:1 대화로 문의해 주세요.`,
          };
          const msg = handledToast[payload?.reason];
          if (msg) showToast({ type: 'info', message: msg });
          cleanupLocal();
          return;
        }
        case 'rs:signal':
          if (sid === sessionRef.current) void peerRef.current?.handleSignal(payload.data);
          return;
        case 'rs:control:requested': {
          if (sid !== sessionRef.current) return;
          void (async () => {
            try {
              // Rust가 직접 띄우는 동의창 — 사람이 [허용]을 눌러야만 토큰이 나온다
              const token = await rsInvoke<string>('rs_control_begin', {
                sessionId: sid,
                operatorName: operatorRef.current,
              });
              if (sessionRef.current !== sid) {
                rsInvoke('rs_control_end').catch(() => {});
                return;
              }
              tokenRef.current = token;
              const ack = await socketService.emitRemoteSupportWithAck('rs:control:granted', { sessionId: sid });
              if (!ack.ok) {
                rsInvoke('rs_control_end').catch(() => {});
                dropControl();
                return;
              }
              setControlling(true);
            } catch (err) {
              const reason = String(err).includes('timeout') ? 'timeout' : 'declined';
              emit('rs:control:denied', { sessionId: sid, reason });
            }
          })();
          return;
        }
        case 'rs:control:revoked': {
          if (sid !== sessionRef.current) return;
          if (tokenRef.current) rsInvoke('rs_control_end').catch(() => {});
          dropControl();
          if (payload?.by !== 'host') {
            showToast({ type: 'info', message: CONTROL_END_TOAST[payload?.reason] || '제어가 끝났습니다.' });
          }
          return;
        }
        case 'rs:input':
          if (sid === sessionRef.current && Array.isArray(payload?.events)) {
            pumpRef.current?.push(payload.events.slice(0, 200));
          }
          return;
        case 'rs:ended': {
          if (sid !== sessionRef.current && !endedRef.current) {
            // 우리가 먼저 정리한 세션의 최종 숫자 — 요약을 서버 값으로 보여 준다
            if (payload?.code && payload.code === codeRef.current) {
              showSummary({
                operatorName: payload.operatorName || operatorRef.current,
                code: payload.code,
                durationSeconds: payload.durationSeconds ?? null,
                controlSeconds: payload.controlSeconds ?? 0,
                reason: payload.reason,
                filesToHost: payload.filesToHost ?? 0,
                filesToOperator: payload.filesToOperator ?? 0,
              });
            }
            return;
          }
          if (sid !== sessionRef.current) return;
          const wasViewing = !!codeRef.current;
          cleanupLocal();
          if (wasViewing) {
            showSummary({
              operatorName: payload.operatorName || operatorRef.current,
              code: payload.code ?? codeRef.current,
              durationSeconds: payload.durationSeconds ?? null,
              controlSeconds: payload.controlSeconds ?? 0,
              reason: payload.reason,
              filesToHost: payload.filesToHost ?? 0,
              filesToOperator: payload.filesToOperator ?? 0,
            });
          }
          return;
        }
        case 'rs:__connect': {
          // 끊긴 사이 서버가 세션을 끝냈다(직원 소켓이 사라지면 서버는 즉시 정리한다)
          if (sessionRef.current) {
            cleanupLocal();
            showToast({ type: 'info', message: '연결이 끊겨 원격지원이 끝났습니다.' });
          }
          return;
        }
        default:
      }
    });
  }, [cleanupLocal, dropControl, showSummary, showToast, startAllScreens]);

  // 입력 펌프 — 게이트가 닫혔다는 응답이면 서버에 회수를 알린다
  useEffect(() => {
    pumpRef.current = new InjectPump(
      () => tokenRef.current,
      (reason) => {
        const sid = sessionRef.current;
        if (!tokenRef.current || !sid) return;
        dropControl();
        const r = reason.includes('expired') ? 'expired' : reason.includes('idle') ? 'idle' : 'gate_closed';
        emit('rs:control:revoke', { sessionId: sid, reason: r, inputs: takeInputs() });
      }
    );
  }, [dropControl]);

  // ── Rust 이벤트: 상태 바·트레이·단축키, 게이트 만료, 보안 데스크톱 ──

  useEffect(() => {
    const offStop = listenNative<{ source: string; sessionId: string }>('remote-support-stop', (p) => {
      if (p?.sessionId && p.sessionId !== sessionRef.current) return;
      endSession('normal');
    });
    const offCtl = listenNative<{ reason: string; sessionId: string }>('remote-support-control-ended', (p) => {
      const sid = sessionRef.current;
      if (!sid || (p?.sessionId && p.sessionId !== sid)) return;
      const hadToken = !!tokenRef.current;
      dropControl();
      if (!hadToken) return;
      emit('rs:control:revoke', { sessionId: sid, reason: p?.reason || 'host_revoked', inputs: takeInputs() });
      showToast({ type: 'info', message: CONTROL_END_TOAST[p?.reason] || '제어를 되찾았습니다.' });
    });
    const offSecure = listenNative<boolean>('remote-support-secure-desktop', (secure) => {
      const sid = sessionRef.current;
      if (!sid || !codeRef.current) return;
      emit('rs:status', { sessionId: sid, payload: { secureDesktop: !!secure } });
    });
    // 모니터를 꽂거나 뺐다 — 담당자 뷰어의 '화면 1·2' 확대가 새 배치를 쓰도록 PC 정보(capture·monitors)를 다시 보낸다
    const offLayout = listenNative<SessionLayout>('remote-support-layout-changed', () => {
      const sid = sessionRef.current;
      if (sid && codeRef.current) sendPcInfo(sid);
    });
    return () => {
      offStop();
      offCtl();
      offSecure();
      offLayout();
    };
  }, [dropControl, endSession, sendPcInfo, showToast]);

  // 언마운트(로그아웃) — 진행 중이면 끝낸다
  useEffect(() => () => {
    const sid = sessionRef.current;
    if (sid) socketService.emitRemoteSupport('rs:end', { sessionId: sid, reason: 'normal' });
    peerRef.current?.close();
    captureRef.current?.stop();
    streamRef.current?.getTracks().forEach(t => t.stop());
    rsInvoke('rs_session_end').catch(() => {});
  }, []);

  // ── 렌더 ──

  const askRemaining = asking ? Math.max(0, Math.ceil((asking.expiresAt - now) / 1000)) : 0;
  const elapsed = startedAt ? Math.max(0, Math.round((now - startedAt) / 1000)) : 0;
  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`;

  return (
    <>
      {/* 내가 담당자에게 요청하고 수락을 기다리는 중 — 기다리는 동안 대화는 계속할 수 있게 막지 않는다 */}
      {phase === 'asking' && asking && (
        <div
          role="status"
          className="fixed bottom-6 right-6 z-50 w-[min(20rem,calc(100vw-2rem))] rounded-2xl bg-white border border-slate-200 shadow-xl p-4"
        >
          <div className="flex items-center gap-3">
            <img
              src={resolveAvatarUrl(asking.target.avatar)}
              onError={handleAvatarError}
              alt=""
              className="w-10 h-10 rounded-full object-cover bg-slate-100 shrink-0"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                <LifeBuoy size={16} className="text-slate-500 shrink-0" aria-hidden="true" />
                원격지원 요청
              </p>
              <p className="text-xs text-slate-500 break-anywhere">
                {asking.target.name}님의 수락을 기다리는 중 · {askRemaining}초
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={cancelAsk}
            className="mt-3 w-full px-4 py-2 text-sm font-medium rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
          >
            요청 취소
          </button>
        </div>
      )}

      {/* 진행 중 배너 — 작업을 막지 않는다(Modal 아님). 화면 위 네이티브 막대와 같은 동작.
          consenting 동안은 요청한 담당자의 사진을 여기서 보여 준다(네이티브 확인창은 사진을 그리지 않는다).
          동의는 네이티브 확인창에서만 받는다 — 여기에는 [공유]가 없고 거절만 있다 */}
      {(phase === 'viewing' || phase === 'starting' || phase === 'consenting') && (
        <div
          role="status"
          className="fixed left-1/2 -translate-x-1/2 top-[calc(var(--titlebar-h,0px)+0.5rem)] z-[70] max-w-[calc(100vw-2rem)] flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-full bg-slate-900 text-white shadow-lg text-sm"
        >
          {phase === 'consenting' && incoming ? (
            <img
              src={resolveAvatarUrl(incoming.operator.avatar)}
              onError={handleAvatarError}
              alt=""
              className="-ml-1.5 w-6 h-6 rounded-full object-cover bg-slate-700 shrink-0"
            />
          ) : (
            <>
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${controlling ? 'bg-brand-500' : 'bg-orange-500'}`} aria-hidden="true" />
              {controlling ? (
                <MousePointer2 size={14} className="shrink-0" aria-hidden="true" />
              ) : (
                <ScreenShare size={14} className="shrink-0" aria-hidden="true" />
              )}
            </>
          )}
          <span className="truncate">
            {phase === 'viewing'
              ? `${operatorRef.current}님이 ${controlling ? '제어하는 중' : '화면을 보는 중'} · ${mmss}`
              : phase === 'consenting'
                ? `${incoming?.operator.name || '담당자'}님의 원격지원 ${incoming?.asked ? '수락' : '요청'} · 주 모니터 가운데 확인창에서 [공유]를 눌러 주세요`
                : '원격지원을 준비하는 중'}
          </span>
          {controlling && (
            <button
              type="button"
              onClick={() => {
                rsInvoke('rs_control_end').catch(() => {});
                const sid = sessionRef.current;
                dropControl();
                if (sid) emit('rs:control:revoke', { sessionId: sid, reason: 'host_revoked', inputs: takeInputs() });
              }}
              className="shrink-0 px-2.5 py-1 rounded-full border border-white/40 hover:bg-white/10 text-xs font-medium"
            >
              제어 중지
            </button>
          )}
          {phase === 'consenting' ? (
            // 확인창을 닫는 것과 같다(rs_session_end가 확인창도 걷는다). 거절은 좁히는 전이라 JS에 두어도 된다
            <button
              type="button"
              onClick={() => decline('declined')}
              className="shrink-0 px-2.5 py-1 rounded-full border border-white/40 hover:bg-white/10 text-xs font-medium"
            >
              {incoming?.asked ? '취소' : '거절'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => (phase === 'viewing' ? endSession('normal') : decline('picker'))}
              className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-600 hover:bg-brand-700 text-xs font-medium"
            >
              <X size={12} aria-hidden="true" />
              종료
            </button>
          )}
        </div>
      )}

      {/* 파일 주고받기 — 동의 모달·진행 카드. 데이터는 P2P('fx'), 서버는 허가·감사만 */}
      <RemoteFileHost channel={fileChannel} sessionId={fileChannel ? sessionRef.current : null} operatorName={operatorRef.current} />

      {/* 종료 요약 — 설정의 '원격지원 기록'에서 다시 볼 수 있다 */}
      <Modal isOpen={!!summary} onClose={() => setSummary(null)} title="원격지원이 끝났습니다" size="sm">
        {summary && (
          <div className="space-y-3 text-sm">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
              <dt className="text-slate-500">담당자</dt>
              <dd className="text-slate-800">{summary.operatorName}</dd>
              <dt className="text-slate-500">총 시간</dt>
              <dd className="text-slate-800 tabular-nums">
                {formatDuration(summary.durationSeconds)}
                {summary.controlSeconds > 0 && ` (제어 ${formatDuration(summary.controlSeconds)})`}
              </dd>
              {summary.code && (
                <>
                  <dt className="text-slate-500">세션 번호</dt>
                  <dd className="text-slate-800 tabular-nums">{summary.code}</dd>
                </>
              )}
              {filesLabel(summary.filesToHost, summary.filesToOperator, 'host') && (
                <>
                  <dt className="text-slate-500">파일</dt>
                  <dd className="text-slate-800">{filesLabel(summary.filesToHost, summary.filesToOperator, 'host')}</dd>
                </>
              )}
              <dt className="text-slate-500">종료</dt>
              <dd className="text-slate-800">{endReasonLabel(summary.reason)}</dd>
            </dl>
            <p className="text-xs text-slate-500">이 기록은 내 프로필 설정의 원격지원 기록에서 다시 볼 수 있습니다.</p>
          </div>
        )}
      </Modal>
    </>
  );
};
